import React from 'react';
import CloumsCenter from '@intelligence/components/TableComponents/components/CloumsCenter';
import SortBy from '@intelligence/components/TableComponents/components/SortBy';
import TableShowData from '@intelligence/components/TableComponents/components/TableShowData';
import { getGameRankDetails } from '@intelligence/utils/tableUtils';
import { formatRobloxRankChangeGrowth } from '@intelligence/v3/utils/robloxGrowthFormat';
import { getActualWidthOfChars } from '@intelligence/utils/commonUtils';
import {
  formatRankValue,
} from '@intelligence/_config/storeRank/utils';
import { getRobloxRankSourceItemByValue } from '../../config/robloxRankSourceConfig';
import { ROBLOX_DEVICE_SELECT_OPTIONS } from '../../config/robloxDeviceFilter';
import { ROBLOX_GAME_RANK_SORT_KEY, ROBLOX_GAME_RANK_SORT_KEY_DOD } from '../../constants';
import {
  formatRobloxTableGenreLabel,
  type TRobloxGenreLabelMap,
} from '@intelligence/v3/utils/robloxGenreLabel';

import type { SortByItemType } from '@intelligence/components/TableComponents/components/SortBy';
import type { IntlShape } from 'react-intl';
import type { IRobloxGameRankRow } from '../../types';

const PLACEHOLDER = '-';
const SOURCE_COLUMN_MIN_WIDTH = 80;
const SOURCE_COLUMN_PADDING = 40;
const SOURCE_COLUMN_SORT_ICON_WIDTH = 28;
const SOURCE_COLUMN_RANK_CHANGE_WIDTH = 76;
const RELEASE_DATE_COLUMN_WIDTH = 140;
const UP_DATE_COLUMN_WIDTH = 140;
const DEVICE_COLUMN_WIDTH = 160;
const ROBLOX_GENRE_COLUMN_WIDTH = 220;
const ROBLOX_TABLE_SHOW_DATA_CELL_CLASS = 'dbi-roblox-table-show-data-cell';
const ROBLOX_TABLE_HEADER_CLASS = 'dbi-roblox-table-metric-header';

const renderRightAlignedHeader = (
  columnTitle: string,
  columnWidth: number,
) => (
  <CloumsCenter width={columnWidth} align="right">
    <SortBy
      className={ROBLOX_TABLE_HEADER_CLASS}
      title={columnTitle}
      defaultKey=""
      defaultValue=""
      align="right"
      isNoDropdown
    />
  </CloumsCenter>
);

const renderLeftAlignedHeader = (
  columnTitle: string,
  columnWidth: number,
) => (
  <CloumsCenter width={columnWidth} align="left">
    <SortBy
      className={ROBLOX_TABLE_HEADER_CLASS}
      title={columnTitle}
      defaultKey=""
      defaultValue=""
      align="left"
      isNoDropdown
    />
  </CloumsCenter>
);

const formatTableDate = (value?: string): string => {
  if (!value) {
    return PLACEHOLDER;
  }

  return value;
};

const getRobloxDeviceLabel = (
  device: string | undefined,
  formatMessage: IntlShape['formatMessage'],
): string => {
  if (!device) {
    return PLACEHOLDER;
  }

  const matchedOption = ROBLOX_DEVICE_SELECT_OPTIONS.find((option) => option.value === device);
  if (matchedOption) {
    return formatMessage({ id: matchedOption.text });
  }

  return device;
};

const RobloxGenreCell: React.FC<{
  mainGenre?: string;
  subGenre?: string;
  formatMessage: IntlShape['formatMessage'];
}> = ({ mainGenre, subGenre, formatMessage }) => {
  const main = mainGenre || PLACEHOLDER;
  const sub = subGenre || PLACEHOLDER;

  return (
    <div className="general_table_genre">
      <p className="main_genre_general">
        <span>
          {formatMessage({ id: 'dbi_genre_main' })} : {main}
        </span>
      </p>
      <p className="sub_genre_general">
        <span>
          {formatMessage({ id: 'dbi_genre_sub' })} : {sub}
        </span>
      </p>
    </div>
  );
};

const getRobloxRankSourceLabel = (
  sourceValue: string | undefined,
  formatMessage: IntlShape['formatMessage'],
): string => {
  if (!sourceValue) {
    return PLACEHOLDER;
  }

  const sourceItem = getRobloxRankSourceItemByValue(sourceValue);
  if (sourceItem) {
    return formatMessage({ id: sourceItem.labelKey });
  }

  return sourceValue;
};

const getRowRankForSource = (
  row: IRobloxGameRankRow,
  selectedSource: string,
): number | null | undefined => {
  if (row?.rank !== null && row?.rank !== undefined) {
    return row.rank;
  }

  const matchedDetail = row?.rank_details?.find(
    (item) => item?.source === selectedSource,
  );

  return matchedDetail?.rank;
};

const getRowRankDodForSource = (
  row: IRobloxGameRankRow,
  selectedSource: string,
): number | null | undefined => {
  if (row?.rank_dod !== null && row?.rank_dod !== undefined) {
    return row.rank_dod;
  }

  const matchedDetail = row?.rank_details?.find(
    (item) => item?.source === selectedSource,
  );

  return matchedDetail?.rank_dod;
};

const getRobloxRankSourceColumnWidth = (columnTitle: string): number => {
  const titleWidth = getActualWidthOfChars(columnTitle, {
    fontSize: 14,
    fontWeight: 700,
  });

  return Math.max(
    SOURCE_COLUMN_MIN_WIDTH,
    Math.ceil(titleWidth)
      + SOURCE_COLUMN_PADDING
      + SOURCE_COLUMN_SORT_ICON_WIDTH
      + SOURCE_COLUMN_RANK_CHANGE_WIDTH,
  );
};

interface IGetRobloxGameRankSourceColumnOptions {
  currentSortKey: string;
  currentSortValue: 'asc' | 'desc';
  onSortChange: (payload: SortByItemType) => void;
}

export const getRobloxGameRankSourceColumn = (
  formatMessage: IntlShape['formatMessage'],
  selectedSource: string,
  options: IGetRobloxGameRankSourceColumnOptions,
) => {
  const { currentSortKey, currentSortValue, onSortChange } = options;
  const columnTitle = getRobloxRankSourceLabel(selectedSource, formatMessage);
  const columnWidth = getRobloxRankSourceColumnWidth(columnTitle);
  const sourceItem = getRobloxRankSourceItemByValue(selectedSource);
  const titleContent = sourceItem
    ? formatMessage({ id: sourceItem.descriptionKey })
    : undefined;
  const rankChangeLabel = formatMessage({ id: 'dbi_change' });

  return {
    key: 'source',
    align: 'right',
    width: columnWidth,
    header: (
      <CloumsCenter width={columnWidth} align="right">
        <SortBy
          className={`dbi-roblox-rank-source-header ${ROBLOX_TABLE_HEADER_CLASS}`}
          title={columnTitle}
          defaultKey={currentSortKey}
          defaultValue={currentSortValue}
          align="right"
          list={[
            {
              key: ROBLOX_GAME_RANK_SORT_KEY,
              sortMetric: 'rank',
              text: columnTitle,
              title: columnTitle,
              ratioType: 0,
            },
            {
              key: ROBLOX_GAME_RANK_SORT_KEY_DOD,
              sortMetric: 'rank_dod',
              text: `${columnTitle}:${rankChangeLabel}`,
              title: `${columnTitle}:${rankChangeLabel}`,
              ratioType: 1,
            },
          ]}
          onChange={onSortChange}
          content={titleContent}
        />
      </CloumsCenter>
    ),
    render: (row: IRobloxGameRankRow) => {
      const rankValue = getRowRankForSource(row, selectedSource);
      const rankDodValue = getRowRankDodForSource(row, selectedSource);
      const { rankText } = getGameRankDetails(rankValue ?? undefined);
      const growthDataObj = formatRobloxRankChangeGrowth(rankDodValue);

      return (
        <CloumsCenter
          width={columnWidth}
          align="right"
          classNameValue={ROBLOX_TABLE_SHOW_DATA_CELL_CLASS}
        >
          <TableShowData
            maxNumber="99999+"
            value={rankText === '-' ? rankText : formatRankValue(rankValue)}
            type={growthDataObj?.type}
            growth={growthDataObj?.value}
          />
        </CloumsCenter>
      );
    },
  };
};

interface IRobloxGenreColumnOptions {
  robloxGenreLabelMap?: TRobloxGenreLabelMap;
  isChinese?: boolean;
}

export const getRobloxGameRankMetricColumns = (
  formatMessage: IntlShape['formatMessage'],
  genreColumnOptions?: IRobloxGenreColumnOptions,
) => {
  const robloxGenreLabelMap = genreColumnOptions?.robloxGenreLabelMap || new Map();
  const isChinese = genreColumnOptions?.isChinese ?? false;
  const formatGenreLabel = (genreKey?: string | null): string =>
    formatRobloxTableGenreLabel(genreKey, robloxGenreLabelMap, isChinese, PLACEHOLDER);

  return [
    {
      key: 'release_time',
      align: 'left',
      width: RELEASE_DATE_COLUMN_WIDTH,
      header: renderLeftAlignedHeader(
        formatMessage({ id: 'dbi_release_date' }),
        RELEASE_DATE_COLUMN_WIDTH,
      ),
      render: (row: IRobloxGameRankRow) => (
        <CloumsCenter width={RELEASE_DATE_COLUMN_WIDTH} align="left">
          {formatTableDate(row?.release_time)}
        </CloumsCenter>
      ),
    },
    {
      key: 'update_time',
      align: 'left',
      width: UP_DATE_COLUMN_WIDTH,
      header: renderLeftAlignedHeader(
        formatMessage({ id: 'dbi_roblox_up_date' }),
        UP_DATE_COLUMN_WIDTH,
      ),
      render: (row: IRobloxGameRankRow) => (
        <CloumsCenter width={UP_DATE_COLUMN_WIDTH} align="left">
          {formatTableDate(row?.update_time)}
        </CloumsCenter>
      ),
    },
    {
      key: 'device',
      align: 'left',
      width: DEVICE_COLUMN_WIDTH,
      header: renderLeftAlignedHeader(
        formatMessage({ id: 'dbi_roblox_device' }),
        DEVICE_COLUMN_WIDTH,
      ),
      render: (row: IRobloxGameRankRow) => (
        <CloumsCenter width={DEVICE_COLUMN_WIDTH} align="left">
          {getRobloxDeviceLabel(row?.device, formatMessage)}
        </CloumsCenter>
      ),
    },
    {
      key: 'roblox_genre',
      align: 'left',
      width: ROBLOX_GENRE_COLUMN_WIDTH,
      header: renderLeftAlignedHeader(
        formatMessage({ id: 'dbi_roblox_genre' }),
        ROBLOX_GENRE_COLUMN_WIDTH,
      ),
      render: (row: IRobloxGameRankRow) => (
        <RobloxGenreCell
          mainGenre={formatGenreLabel(
            row?.roblox_sub_genre ?? row?.entity_info?.roblox_sub_genre,
          )}
          subGenre={formatGenreLabel(
            row?.roblox_level_3_genre ?? row?.entity_info?.roblox_level_3_genre,
          )}
          formatMessage={formatMessage}
        />
      ),
    },
  ];
};
